import React, { useState } from 'react';
import { useAuth } from './AuthContext';

const ProfileModal = ({ isOpen, onClose }) => {
  const { user, updateProfile, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('profile'); // 'profile' or 'password'
  const [formData, setFormData] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    company: user?.company || '',
    location: user?.location || '',
  });
  const [supplierIds, setSupplierIds] = useState(user?.supplierIds || []);
  const [supplierInput, setSupplierInput] = useState('');
  const [supplierInputError, setSupplierInputError] = useState('');
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [errors, setErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const handleAddSupplierId = () => {
    const trimmed = supplierInput.trim();
    if (!trimmed) return;
    const num = parseInt(trimmed, 10);
    if (isNaN(num) || num <= 0 || String(num) !== trimmed) {
      setSupplierInputError('Bitte eine gültige positive Zahl eingeben');
      return;
    }
    if (supplierIds.includes(num)) {
      setSupplierInputError('Diese ID wurde bereits hinzugefügt');
      return;
    }
    setSupplierIds(prev => [...prev, num]);
    setSupplierInput('');
    setSupplierInputError('');
  };

  const handleSupplierInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSupplierId();
    }
  };

  const handleRemoveSupplierId = (id) => {
    setSupplierIds(prev => prev.filter(s => s !== id));
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (passwordErrors[name]) {
      setPasswordErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validatePasswordForm = () => {
    const newErrors = {};

    if (!passwordData.old_password) {
      newErrors.old_password = 'Aktuelles Passwort ist erforderlich';
    }

    if (!passwordData.new_password) {
      newErrors.new_password = 'Neues Passwort ist erforderlich';
    } else if (passwordData.new_password.length < 8) {
      newErrors.new_password = 'Neues Passwort muss mindestens 8 Zeichen lang sein';
    }

    if (!passwordData.confirm_password) {
      newErrors.confirm_password = 'Passwort-Bestätigung ist erforderlich';
    } else if (passwordData.new_password !== passwordData.confirm_password) {
      newErrors.confirm_password = 'Passwörter stimmen nicht überein';
    }

    if (passwordData.old_password === passwordData.new_password) {
      newErrors.new_password = 'Neues Passwort muss sich vom aktuellen unterscheiden';
    }

    setPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePasswordForm()) return;

    setPasswordLoading(true);
    // TODO: Backend endpoint POST /api/auth/change-password not yet implemented
    // Stub: show not available message
    setPasswordErrors({ general: 'Funktion noch nicht verfügbar (Backend in Entwicklung)' });
    setPasswordLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Name ist erforderlich';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'E-Mail ist erforderlich';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Ungültige E-Mail-Adresse';
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    
    try {
      const updateData = {
        ...formData,
        phone: formData.phone || null,
        company: formData.company || null,
        location: formData.location || null,
        supplierIds: supplierIds,
      };
      
      const result = await updateProfile(updateData);
      
      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
          onClose();
        }, 2000);
      } else {
        setErrors({ general: result.error });
      }
    } catch (error) {
      setErrors({ general: 'Ein unerwarteter Fehler ist aufgetreten' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header with Tabs */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Profil bearbeiten</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex space-x-0 border-b border-gray-200 mb-6">
            <button
              onClick={() => setActiveTab('profile')}
              className={`px-4 py-2 font-medium text-sm transition-all duration-200 border-b-2 ${
                activeTab === 'profile' 
                  ? 'border-orange-500 text-orange-600 bg-orange-50' 
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
            >
              Profildaten
            </button>
            <button
              onClick={() => setActiveTab('password')}
              className={`px-4 py-2 font-medium text-sm transition-all duration-200 border-b-2 ${
                activeTab === 'password' 
                  ? 'border-orange-500 text-orange-600 bg-orange-50' 
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
            >
              Passwort ändern
            </button>
          </div>

          {/* User Info */}
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-600">Benutzername</p>
            <p className="font-medium text-gray-900">{user?.username}</p>
            <p className="text-xs text-gray-500 mt-1">
              Registriert seit: {new Date(user?.createdAt).toLocaleDateString('de-DE')}
            </p>
            {user?.last_login && (
              <p className="text-xs text-gray-500">
                Letzter Login: {new Date(user.last_login).toLocaleString('de-DE')}
              </p>
            )}
          </div>

          {activeTab === 'profile' ? (
            // Profile Tab Content
            <>
              {errors.general && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                  <p className="text-sm text-red-600">{errors.general}</p>
                </div>
              )}

              {success && (
                <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4">
                  <p className="text-sm text-green-600">Profil erfolgreich aktualisiert!</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Full Name */}
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                    Vollständiger Name *
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                      errors.fullName ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Max Mustermann"
                  />
                  {errors.fullName && <p className="text-xs text-red-600 mt-1">{errors.fullName}</p>}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                      errors.email ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="ihre@email.de"
                  />
                  {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
                </div>

                {/* Phone and Company */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Telefon
                    </label>
                    <input
                      type="text"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="+49 30 12345678"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                      Firma
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="FastPlanet GmbH"
                    />
                  </div>
                </div>

                {/* Location */}
                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                    Standort
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Berlin"
                  />
                </div>

                {/* Supplier IDs */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lieferanten-IDs
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {supplierIds.map(id => (
                      <span
                        key={id}
                        className="inline-flex items-center px-2 py-1 bg-orange-50 border border-orange-200 text-orange-800 text-xs rounded-full"
                      >
                        #{id}
                        <button
                          type="button"
                          onClick={() => handleRemoveSupplierId(id)}
                          className="ml-1 text-orange-400 hover:text-orange-600 focus:outline-none"
                        >
                          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex space-x-2">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={supplierInput}
                        onChange={(e) => setSupplierInput(e.target.value)}
                        onKeyDown={handleSupplierInputKeyDown}
                        placeholder="ID eingeben"
                        className={`w-full px-3 py-1 border rounded-md shadow-sm text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 ${
                          supplierInputError ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleAddSupplierId}
                      className="px-3 py-1 bg-gray-100 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-200 focus:outline-none"
                    >
                      Hinzufügen
                    </button>
                  </div>
                  {supplierInputError && <p className="text-xs text-red-600 mt-1">{supplierInputError}</p>}
                </div>

                {/* Buttons */}
                <div className="flex space-x-3 pt-6 border-t">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Speichern...
                      </div>
                    ) : (
                      'Speichern'
                    )}
                  </button>
                  
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                  >
                    Abbrechen
                  </button>
                </div>
              </form>
            </>
          ) : (
            // Password Change Tab Content
            <>
              {passwordErrors.general && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                  <p className="text-sm text-red-600">{passwordErrors.general}</p>
                </div>
              )}

              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                {/* Current Password */}
                <div>
                  <label htmlFor="old_password" className="block text-sm font-medium text-gray-700 mb-1">
                    Aktuelles Passwort *
                  </label>
                  <input
                    type="password"
                    id="old_password"
                    name="old_password"
                    value={passwordData.old_password}
                    onChange={handlePasswordInputChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                      passwordErrors.old_password ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Aktuelles Passwort eingeben"
                  />
                  {passwordErrors.old_password && <p className="text-xs text-red-600 mt-1">{passwordErrors.old_password}</p>}
                </div>

                {/* New Password */}
                <div>
                  <label htmlFor="new_password" className="block text-sm font-medium text-gray-700 mb-1">
                    Neues Passwort *
                  </label>
                  <input
                    type="password"
                    id="new_password"
                    name="new_password"
                    value={passwordData.new_password}
                    onChange={handlePasswordInputChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                      passwordErrors.new_password ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Neues Passwort (mindestens 8 Zeichen)"
                  />
                  {passwordErrors.new_password && <p className="text-xs text-red-600 mt-1">{passwordErrors.new_password}</p>}
                </div>

                {/* Confirm New Password */}
                <div>
                  <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700 mb-1">
                    Neues Passwort bestätigen *
                  </label>
                  <input
                    type="password"
                    id="confirm_password"
                    name="confirm_password"
                    value={passwordData.confirm_password}
                    onChange={handlePasswordInputChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                      passwordErrors.confirm_password ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Neues Passwort wiederholen"
                  />
                  {passwordErrors.confirm_password && <p className="text-xs text-red-600 mt-1">{passwordErrors.confirm_password}</p>}
                </div>

                {/* Password Requirements Info */}
                <div className="bg-blue-50 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-blue-800 mb-1">Passwort-Anforderungen:</h4>
                  <ul className="text-xs text-blue-600 space-y-1">
                    <li>• Mindestens 8 Zeichen lang</li>
                    <li>• Muss sich vom aktuellen Passwort unterscheiden</li>
                    <li>• Bestätigung muss übereinstimmen</li>
                  </ul>
                </div>

                {/* Buttons */}
                <div className="flex space-x-3 pt-6 border-t">
                  <button
                    type="submit"
                    disabled={passwordLoading}
                    className="flex-1 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {passwordLoading ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Passwort ändern...
                      </div>
                    ) : (
                      'Passwort ändern'
                    )}
                  </button>
                  
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                  >
                    Abbrechen
                  </button>
                </div>
              </form>
            </>
          )}

          {/* Logout Button */}
          <div className="pt-4 border-t">
            <button
              type="button"
              onClick={handleLogout}
              className="w-full py-2 px-4 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Abmelden
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileModal;